import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { XCircle } from '@phosphor-icons/react';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useInput, useTestId } from '../../hooks';
import { forwardRefWithName } from '../../utils';
import { sendInputEvent } from '../../utils/sendInputEvent';
import { NumberInput } from './NumberInput';
import { themes } from './themes';
function IconComponent(_a, ref) {
    var { icon } = _a, props = __rest(_a, ["icon"]);
    return (_jsx("div", Object.assign({ ref: ref, className: "prefix-suffix", "data-testid": "prefix-element" }, props, { children: icon })));
}
export const variantClassNames = {
    mini: 'input-mini',
    small: 'input-small',
    medium: 'input-medium',
    large: 'input-large',
};
export const getInputClass = (variant) => variantClassNames[variant];
const isNumberInputProps = (props) => typeof props.numberFormat !== 'undefined';
function InputComponent(props, ref) {
    const { variant = 'medium', type = 'text', className, prefixElement, suffixElement, error, // eslint-disable-line @typescript-eslint/no-unused-vars
    disabled = false, canClear = false, expandable = false, initialWidth = 'w-1/2', testId = 'input', theme = 'default' } = props, other = __rest(props, ["variant", "type", "className", "prefixElement", "suffixElement", "error", "disabled", "canClear", "expandable", "initialWidth", "testId", "theme"]);
    const isNumberInput = isNumberInputProps(props);
    const _a = useInput('input', props), { value, setValue, inputClasses, focus } = _a, _b = _a.events, { onChange } = _b, events = __rest(_b, ["onChange"]);
    const tid = useTestId(testId, props);
    const numberInput = useRef();
    const textInput = useRef(null);
    const isInputExpanded = !expandable || focus || (value && value.length > 0);
    const classList = twMerge('input', ...inputClasses, getInputClass(variant), !isInputExpanded ? initialWidth : '', themes[theme], className);
    const getElement = () => (isNumberInput ? numberInput.current : textInput.current);
    const setInputRef = (ref) => (numberInput.current = ref);
    const focusInput = () => {
        var _a;
        (_a = getElement()) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const resetValue = (event) => {
        setValue('');
        event.preventDefault();
        const element = getElement();
        if (element)
            sendInputEvent(element);
    };
    const inputProps = Object.assign(Object.assign(Object.assign({ className: 'flex-auto', disabled,
        type,
        value }, other), tid('input')), { onChange, 'aria-invalid': props.error });
    return (_jsxs("div", Object.assign({ ref: ref, className: classList, onClick: focusInput }, events, tid(), { children: [prefixElement ? _jsx(Input.Icon, Object.assign({ icon: prefixElement }, tid('prefix-element'))) : null, isNumberInput ? (_jsx(NumberInput, Object.assign({ numberFormat: props.numberFormat, getInputRef: setInputRef }, inputProps, { className: twMerge(inputProps.className, !prefixElement ? 'zero-margin' : '') }))) : (_jsx("input", Object.assign({ ref: textInput }, inputProps))), canClear && value && (_jsx("button", Object.assign({ className: "input__reset-button", "aria-label": "clear", onClick: resetValue }, tid('clear-button'), { children: _jsx(XCircle, { size: 20 }) }))), suffixElement ? _jsx(Input.Icon, Object.assign({ icon: suffixElement }, tid('suffix-element'))) : null] })));
}
export const Input = Object.assign(forwardRefWithName(InputComponent), {
    Icon: forwardRefWithName(IconComponent),
});
//# sourceMappingURL=Input.js.map